/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: utils\extractOptions.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Implementation of the Provisioning Device Models module.
────────────────────────────────────────────────────────────────*/

export interface OptionWithSelected {
    value: string;
    label: string;
    selected: boolean;
}

/**
 * Helper function to extract DSS key types from the model's keys field
 * Supports three formats:
 * 1. New format with selected: { dss_keys: number, fields: { type: { values: [{ key, value, selected }, ...] } } }
 * 2. Simple format: { dss_keys: number, supported_types: ['BLF', 'Speed Dial', ...] }
 * 3. Legacy format (from SQL dump): { dss_keys: number, fields: { type: { type: 'select', values: { 'KeyName': 'value', ... } } } }
 */
export function extractDssTypesFromKeys(keys: unknown): OptionWithSelected[] {
    if (!keys) {
        return [];
    }

    try {
        // Handle if keys is a string (JSON)
        const keysObj = typeof keys === 'string' ? JSON.parse(keys) : keys as Record<string, unknown>;

        // Format 1: New format with array of objects containing selected flag
        const typeValues = (keysObj as Record<string, unknown>)?.fields as Record<string, unknown>|undefined;
        const typeValuesArr = (typeValues?.type as Record<string, unknown>)?.values;
        if (Array.isArray(typeValuesArr)) {
            return (typeValuesArr as Array<{ key: string; selected?: boolean }>).map((item) => ({
                value: item.key,
                label: item.key,
                selected: item.selected !== false // Default to true if not specified
            }));
        }

        // Format 2: Simple format with supported_types array
        const keysObj2 = keysObj as Record<string, unknown>;
        if (Array.isArray((keysObj2)?.['supported_types'])) {
            return (keysObj2['supported_types'] as string[]).map((typeName) => ({
                value: typeName,
                label: typeName,
                selected: true // Default all to selected
            }));
        }

        // Format 3: Legacy format with fields.type.values object
        const legacyValues = (typeValues?.type as Record<string, unknown>)?.values;
        if (legacyValues && typeof legacyValues === 'object') {
            return Object.keys(legacyValues).map(typeName => ({
                value: typeName,
                label: typeName,
                selected: true // Default all to selected
            }));
        }


        return [];
    } catch (e) {
        console.warn('[DSS Keys] Failed to parse keys field:', e);
        return [];
    }
}

/**
 * Helper function to extract timezone options from the model's timezone field
 * Supports three formats:
 * 1. New format with selected: [{ key, label, value, selected }, ...]
 * 2. Array format: [{"Africa/Abidjan":"Abidjan|0"}, ...]
 * 3. Object format: {"Africa/Abidjan": "value1", ...}
 */
export function extractTimezoneOptions(timezone: unknown): OptionWithSelected[] {
    if (!timezone) return [];

    try {
        const tzObj = typeof timezone === 'string' ? JSON.parse(timezone) : timezone;

        // Format 1: New array format with selected flag
        if (Array.isArray(tzObj)) {
            return (tzObj as Array<Record<string, unknown>>).map((item) => {
                // New format: { key, label, value, selected }
                if (item['key'] !== undefined) {
                    return {
                        value: String(item['key']),
                        label: String(item['label'] ?? item['key']),
                        selected: item['selected'] !== false
                    };
                }
                // Legacy format: { "Africa/Abidjan": "Abidjan|0" }
                const key = Object.keys(item)[0];
                return { value: key, label: key, selected: true };
            });
        }

        // Format 2: Object with key-value pairs (legacy)
        if (typeof tzObj === 'object') {
            return Object.keys(tzObj).map(key => ({ value: key, label: key, selected: true }));
        }

        return [];
    } catch (e) {
        console.warn('[Timezone] Failed to parse timezone field:', e);
        return [];
    }
}

/**
 * Helper function to extract time format options from the model's timeFormat field
 * Supports new format with selected: [{ key, value, selected }, ...]
 * And legacy object format: {"12 Hour": "value1", "24 Hour": "value2"}
 */
export function extractTimeFormatOptions(timeFormat: unknown): OptionWithSelected[] {
    if (!timeFormat) return [];

    try {
        const tfObj = typeof timeFormat === 'string' ? JSON.parse(timeFormat) : timeFormat;

        // Format 1: New array format with selected flag
        if (Array.isArray(tfObj)) {
            return (tfObj as Array<Record<string, unknown> | string>).map((item) => {
                if (typeof item === 'string') return { value: item, label: item, selected: true };
                // New format: { key, value, selected }
                if (item['key'] !== undefined) {
                    return {
                        value: String(item['key']),
                        label: String(item['key']),
                        selected: item['selected'] !== false
                    };
                }
                // Legacy format: { "12 Hour": "0" }
                const key = Object.keys(item)[0];
                return { value: key, label: key, selected: true };
            });
        }

        // Format 2: Object with key-value pairs (legacy)
        if (typeof tfObj === 'object') {
            return Object.keys(tfObj).map(key => ({ value: key, label: key, selected: true }));
        }

        return [];
    } catch (e) {
        console.warn('[TimeFormat] Failed to parse timeFormat field:', e);
        return [];
    }
}

/**
 * Helper function to extract date format options from the model's dateFormat field
 * Supports new format with selected: [{ key, value, selected }, ...]
 * And legacy object format: {"DD/MM/YYYY": "value1", "MM/DD/YYYY": "value2"}
 */
export function extractDateFormatOptions(dateFormat: unknown): OptionWithSelected[] {
    if (!dateFormat) return [];

    try {
        const dfObj = typeof dateFormat === 'string' ? JSON.parse(dateFormat) : dateFormat;

        // Format 1: New array format with selected flag
        if (Array.isArray(dfObj)) {
            return (dfObj as Array<Record<string, unknown> | string>).map((item) => {
                if (typeof item === 'string') return { value: item, label: item, selected: true };
                // New format: { key, value, selected }
                if (item['key'] !== undefined) {
                    return {
                        value: String(item['key']),
                        label: String(item['key']),
                        selected: item['selected'] !== false
                    };
                }
                // Legacy format: { "DD/MM/YYYY": "0" }
                const key = Object.keys(item)[0];
                return { value: key, label: key, selected: true };
            });
        }

        // Format 2: Object with key-value pairs (legacy)
        if (typeof dfObj === 'object') {
            return Object.keys(dfObj).map(key => ({ value: key, label: key, selected: true }));
        }

        return [];
    } catch (e) {
        console.warn('[DateFormat] Failed to parse dateFormat field:', e);
        return [];
    }
}

/**
 * Helper function to extract language options from the model's language field
 * Format: [{"en": "English", "selected": true}, ...]
 */
export function extractLanguageOptions(language: unknown): OptionWithSelected[] {
    if (!language) return [];

    try {
        const langObj = typeof language === 'string' ? JSON.parse(language) : language;

        // Validating array format
        if (Array.isArray(langObj)) {
            return (langObj as Array<Record<string, unknown>>).map((item) => {
                if (item['code']) { // Format: { code: 'en', name: 'English', selected: true }
                    return {
                        value: String(item['code']),
                        label: String(item['name'] ?? item['code']),
                        selected: item['selected'] !== false
                    };
                }
                // Fallback for simple key-value object in array or legacy format if needed
                // e.g., [{"en": "English"}] is not standard but we can try keys
                const key = Object.keys(item)[0];
                if (key && key !== 'selected' && key !== 'code' && key !== 'name') {
                    return { value: key, label: String(item[key]), selected: true };
                }

                return null;
            }).filter((x): x is OptionWithSelected => x !== null);
        }

        // Object format { "en": "English", ... }
        if (typeof langObj === 'object') {
            const keys = Object.keys(langObj);
            // Check if it's the specific format: '{"English":"English", ...}'
            // Here keys are languages themselves (e.g. English) and values are codes (e.g. English) or vice versa
            // The user provided example: '{"English":"English","Chinese Simplified":"Chinese_S", ...}'
            // We want to transform to: { value: "English", label: "English", selected: true }
            return keys.map(key => ({
                value: langObj[key], // The code/value (e.g. Chinese_S)
                label: key,          // The display name (e.g. Chinese Simplified)
                selected: true
            }));
        }

        return [];
    } catch (e) {
        console.warn('[Language] Failed to parse language field:', e);
        return [];
    }
}
